import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Air } from '@mui/icons-material';
import AppNavigation from './AppNavigation';
import { authService } from '../services/authService';
import { ApneaTableType, NAUI_PHASES } from '../data/nauiTables';
import { formatSeconds, resolveTable } from '../utils/apneaTables';
import { ApneaTimerSet, RecordedSet, readSnapshot } from '../hooks/useApneaTimer';
import { ApneaPlanDto, ApneaProfileDto, ApneaSessionDto, apneaService } from '../services/apneaService';
import { PlanIntake, SESSION_LABELS, buildPlanProposal } from '../utils/apneaPlanBuilder';
import SessionRunner from './apnea/SessionRunner';
import PostSessionLog, { PostSessionValues, buildSessionPayload } from './apnea/PostSessionLog';
import BoltTrendChart from './apnea/BoltTrendChart';
import CheckInPanel, { digestToPrompt } from './apnea/CheckInPanel';
import PlanSetup from './apnea/PlanSetup';
import BoltQuickEntry from './apnea/BoltQuickEntry';
import SessionHistory from './apnea/SessionHistory';
import LearnReference from './apnea/LearnReference';
import FrenzelTracker from './apnea/FrenzelTracker';
import SkillsChecklist from './apnea/SkillsChecklist';

type ApneaTab = 'today' | 'train' | 'plan' | 'log' | 'learn';

const TABS: Array<{ value: ApneaTab; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'train', label: 'Train' },
  { value: 'plan', label: 'Plan' },
  { value: 'log', label: 'Log' },
  { value: 'learn', label: 'Learn' },
];

const isApneaTab = (value: string | null): value is ApneaTab =>
  TABS.some(tab => tab.value === value);

/**
 * Tabs that need a real training plan behind them. Train runs a prescribed table
 * and Log reviews prescribed sessions — neither means much before a plan exists.
 * Today (BOLT entry) and Learn stay open, since they are how the diver builds the
 * baseline a plan setup asks for.
 */
const PLAN_GATED_TABS: ApneaTab[] = ['train', 'log'];

/**
 * Train tab. Pick a NAUI phase and table type, then run it.
 *
 * Session selection is intentionally minimal here — the adaptive plan (group G)
 * will prescribe the phase rather than leaving it to a dropdown, and the rule
 * engine will gate it. This is the runner made reachable, not the final flow.
 */
/** localStorage key for runner crash recovery. */
const RUNNER_PERSIST_KEY = 'apnea.runner.v1';
/** Which table the persisted session belongs to, so it restores into the right shape. */
const RUNNER_TABLE_KEY = 'apnea.runner.table.v1';

interface RunnerTableRef {
  tableType: ApneaTableType;
  phase: number;
  startedAt: string;
}

const readRunnerTable = (): RunnerTableRef | null => {
  try {
    const raw = window.localStorage.getItem(RUNNER_TABLE_KEY);
    return raw ? (JSON.parse(raw) as RunnerTableRef) : null;
  } catch {
    return null;
  }
};

const TrainTab: React.FC<{ onSaved: () => void }> = ({ onSaved }) => {
  // If an unfinished session exists, restore the table it belonged to — otherwise
  // the runner would resume a Phase 1 CO2 snapshot into whatever the dropdown says.
  const resumed = useMemo(
    () => (readSnapshot(RUNNER_PERSIST_KEY) ? readRunnerTable() : null),
    []
  );
  const [tableType, setTableType] = useState<ApneaTableType>(resumed?.tableType ?? 'co2');
  const [phase, setPhase] = useState(resumed?.phase ?? 1);
  // A reload lands here, not in the runner — so mount the runner immediately and
  // let it offer resume-or-discard.
  const [running, setRunning] = useState(() => readSnapshot(RUNNER_PERSIST_KEY) !== null);
  const [pending, setPending] = useState<{
    records: RecordedSet[];
    aborted: boolean;
    abortReason?: string;
    startedAt: Date;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  // Survives a reload via RUNNER_TABLE_KEY, so a recovered session keeps its real
  // start time rather than being stamped with the moment it was recovered.
  const startedAtRef = useRef<Date | null>(
    resumed?.startedAt ? new Date(resumed.startedAt) : null
  );

  const table = useMemo(() => resolveTable(phase, tableType), [phase, tableType]);

  const timerSets: ApneaTimerSet[] = useMemo(
    () => table.sets.map(s => ({ holdSeconds: s.holdSeconds, ventSeconds: s.ventSeconds })),
    [table]
  );

  const handleFinished = (records: RecordedSet[], aborted: boolean, abortReason?: string) => {
    setRunning(false);
    try {
      window.localStorage.removeItem(RUNNER_TABLE_KEY);
    } catch {
      // Storage unavailable; the snapshot itself is cleared by the runner.
    }
    setPending({
      records,
      aborted,
      abortReason,
      startedAt: startedAtRef.current ?? new Date(),
    });
  };

  const handleSave = async (values: PostSessionValues) => {
    if (!pending) return;
    setSaving(true);
    setSaveError(null);
    try {
      const payload = buildSessionPayload(values, {
        type: tableType === 'co2' ? 'Co2' : 'O2',
        startedAt: pending.startedAt,
        completedAt: new Date(),
        phase,
        plannedSets: table.sets.map(s => ({
          set: s.set,
          holdSeconds: s.holdSeconds,
          ventSeconds: s.ventSeconds,
        })),
        aborted: pending.aborted,
        abortReason: pending.abortReason,
      });
      await apneaService.saveSession(payload);
      setPending(null);
      setSavedMessage('Session saved to your log.');
      onSaved();
    } catch (err) {
      // Keep the form mounted so a network failure never loses a completed session.
      setSaveError(err instanceof Error ? err.message : 'Could not save the session.');
    } finally {
      setSaving(false);
    }
  };

  if (running) {
    return (
      <SessionRunner
        sets={timerSets}
        // The first set's ventilation doubles as the initial breathe-up.
        breatheUpSeconds={table.sets[0].ventSeconds}
        includePeakInhalation
        persistKey={RUNNER_PERSIST_KEY}
        onFinished={handleFinished}
        onCancel={() => setRunning(false)}
      />
    );
  }

  if (pending) {
    return (
      <PostSessionLog
        records={pending.records}
        aborted={pending.aborted}
        saving={saving}
        error={saveError}
        onSave={handleSave}
        onDiscard={() => { setPending(null); setSaveError(null); }}
      />
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>NAUI tolerance table</Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <TextField
          select
          label="Table type"
          value={tableType}
          onChange={e => setTableType(e.target.value as ApneaTableType)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="co2">CO₂ — hold constant, rest shrinks</MenuItem>
          <MenuItem value="o2">O₂ — hold grows, rest constant</MenuItem>
        </TextField>

        <TextField
          select
          label="Phase"
          value={phase}
          onChange={e => setPhase(Number(e.target.value))}
          sx={{ minWidth: 140 }}
        >
          {NAUI_PHASES.map(p => (
            <MenuItem key={p.phase} value={p.phase}>Phase {p.phase}</MenuItem>
          ))}
        </TextField>
      </Stack>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        8 sets · longest hold {formatSeconds(table.longestHoldSeconds)} · about{' '}
        {formatSeconds(table.totalSeconds)} total
      </Typography>

      <Stack direction="row" spacing={0.5} sx={{ my: 2, flexWrap: 'wrap', gap: 0.5 }}>
        {table.sets.map(s => (
          <Chip
            key={s.set}
            size="small"
            label={`${formatSeconds(s.holdSeconds)} / ${formatSeconds(s.ventSeconds)}`}
            variant="outlined"
          />
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        shown as hold / rest
      </Typography>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            const startedAt = new Date();
            startedAtRef.current = startedAt;
            try {
              window.localStorage.setItem(RUNNER_TABLE_KEY, JSON.stringify({
                tableType, phase, startedAt: startedAt.toISOString(),
              }));
            } catch {
              // Storage unavailable; the session still runs, just without recovery.
            }
            setSavedMessage(null);
            setRunning(true);
          }}
        >
          Start table
        </Button>
      </Box>

      {savedMessage && (
        <Alert severity="success" sx={{ mt: 3 }}>{savedMessage}</Alert>
      )}
    </Paper>
  );
};

/** Today tab: BOLT quick entry, plus any active safety blocks. */
const TodayTab: React.FC<{ reloadKey: number; onSaved: () => void }> = ({ reloadKey, onSaved }) => {
  const [sessions, setSessions] = useState<ApneaSessionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apneaService.getSessions({ limit: 200 })
      .then(result => { if (!cancelled) setSessions(result); })
      .catch(() => { /* Today still renders; the entry form does not need history. */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const boltHistory = useMemo(
    () => sessions.filter(s => s.bolt).map(s => s.bolt!.seconds),
    [sessions]
  );

  const loggedToday = useMemo(() => {
    const today = new Date().toDateString();
    return sessions.some(s => s.bolt && new Date(s.startedAt).toDateString() === today);
  }, [sessions]);

  const handleSaveBolt = async (seconds: number, source: 'manual' | 'corrected') => {
    setSaving(true);
    setError(null);
    try {
      await apneaService.saveSession({
        type: 'Bolt',
        startedAt: new Date().toISOString(),
        completedAt: new Date().toISOString(),
        plannedSets: [],
        actualSets: [],
        metrics: {},
        warningSigns: { occurred: false, kind: 'None' },
        bolt: { seconds, source: source === 'corrected' ? 'Corrected' : 'Manual' },
        aborted: false,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the reading.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      <BoltQuickEntry
        history={boltHistory}
        loggedToday={loggedToday}
        saving={saving}
        error={error}
        onSave={handleSaveBolt}
      />
    </Stack>
  );
};

/**
 * Learn tab: reference content plus the two self-assessment trackers.
 *
 * Tracker state lives on the apnea profile. Toggles save optimistically so a
 * checkbox never feels laggy, and roll back if the write fails.
 */
const LearnTab: React.FC = () => {
  const location = useLocation();
  const [profile, setProfile] = useState<ApneaProfileDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apneaService.getProfile()
      .then(p => { if (!cancelled) setProfile(p); })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load your progress.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // React Router does not auto-scroll to a #hash on client-side navigation the way
  // a full page load does, so a link from Plan setup into a Learn section needs
  // this. Waits for `loading` to clear, since the target section isn't in the DOM
  // until then.
  useEffect(() => {
    if (loading || !location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading, location.hash]);

  const persist = async (next: ApneaProfileDto, previous: ApneaProfileDto) => {
    setProfile(next);
    setSaving(true);
    setError(null);
    try {
      const saved = await apneaService.saveProfile(next);
      setProfile(saved);
    } catch (err) {
      setProfile(previous);
      setError(err instanceof Error ? err.message : 'Could not save that change.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <LearnReference />

      {profile && (
        <>
          <FrenzelTracker
            progress={profile.frenzelProgress ?? {}}
            saving={saving}
            onToggle={(step, done) => persist(
              {
                ...profile,
                frenzelProgress: { ...(profile.frenzelProgress ?? {}), [String(step)]: done },
              },
              profile
            )}
          />

          <SkillsChecklist
            completed={profile.nauiSkills ?? {}}
            saving={saving}
            onToggle={(skillId, done) => persist(
              { ...profile, nauiSkills: { ...(profile.nauiSkills ?? {}), [skillId]: done } },
              profile
            )}
          />
        </>
      )}
    </Stack>
  );
};

/**
 * Plan tab.
 *
 * Shows the active plan and the check-in when one exists, and the setup flow when
 * it does not. Setup can be reopened to revise the plan — saving creates a new
 * version rather than overwriting, so the check-in history stays traceable.
 */
const PlanTab: React.FC<{
  reloadKey: number;
  onSaved: () => void;
  onPlanStatus: (hasPlan: boolean) => void;
}> = ({ reloadKey, onSaved, onPlanStatus }) => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<ApneaSessionDto[]>([]);
  const [plan, setPlan] = useState<ApneaPlanDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apneaService.getSessions({ limit: 200 }),
      apneaService.getPlans(),
    ])
      .then(([s, plans]) => {
        if (cancelled) return;
        setSessions(s);
        const activePlan = plans[0] ?? null;
        setPlan(activePlan);
        onPlanStatus(activePlan !== null);
        setError(null);
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load your plan.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reloadKey, onPlanStatus]);

  // Offer the diver's own latest measurements as defaults, so setup does not ask
  // for numbers the log already has.
  const latestBolt = useMemo(() => {
    const withBolt = sessions.filter(s => s.bolt);
    return withBolt.length > 0 ? withBolt[0].bolt!.seconds : undefined;
  }, [sessions]);

  const latestMaxStatic = useMemo(() => {
    const attempts = sessions.filter(s => s.maxTest);
    return attempts.length > 0 ? attempts[0].maxTest!.seconds : undefined;
  }, [sessions]);

  const handleSavePlan = async (intake: PlanIntake) => {
    setSaving(true);
    setError(null);
    try {
      const proposal = buildPlanProposal(intake);
      if (proposal.blocked) {
        setError(proposal.blocked);
        return;
      }
      // The server assigns the version and chains `supersedes`.
      const saved = await apneaService.savePlan(proposal.plan);
      setPlan(saved);
      onPlanStatus(true);
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the plan.');
    } finally {
      setSaving(false);
    }
  };

  const toChat = (prompt: string) => {
    // ChatPage auto-sends location.state.initialMessage on mount.
    navigate('/chat', { state: { initialMessage: prompt } });
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  if (!plan || editing) {
    return (
      <Stack spacing={2}>
        {plan && (
          <Alert severity="info" action={
            <Button size="small" onClick={() => setEditing(false)}>Cancel</Button>
          }>
            Revising your plan. Saving creates version {plan.version + 1} — the current one
            is kept.
          </Alert>
        )}
        <PlanSetup
          suggestedBoltSeconds={latestBolt}
          suggestedMaxStaticSeconds={latestMaxStatic}
          saving={saving}
          error={error}
          onSave={handleSavePlan}
          onDiscussWithNereus={toChat}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}
        >
          <Typography variant="subtitle1">Current plan</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" variant="outlined" label={`v${plan.version}`} />
            <Button size="small" onClick={() => setEditing(true)}>Revise</Button>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={0.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
          {plan.co2Phase && (
            <Chip size="small" color="primary" label={`CO₂ phase ${plan.co2Phase}`} />
          )}
          <Chip
            size="small"
            variant="outlined"
            label={plan.o2Phase ? `O₂ phase ${plan.o2Phase}` : 'O₂ locked'}
          />
          {plan.baselines?.maxStaticSeconds && (
            <Chip
              size="small"
              variant="outlined"
              label={`max ${formatSeconds(plan.baselines.maxStaticSeconds)}${
                plan.baselines.isEstimate ? ' (est)' : ''
              }`}
            />
          )}
        </Stack>

        {Object.keys(plan.weeklySchedule ?? {}).length > 0 && (
          <>
            <Typography variant="subtitle2" gutterBottom>Weekly shape</Typography>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
              {Object.entries(plan.weeklySchedule).map(([day, kind]) => (
                <Chip
                  key={day}
                  size="small"
                  variant="outlined"
                  label={`${day.slice(0, 3)} · ${SESSION_LABELS[kind] ?? kind}`}
                />
              ))}
            </Stack>
          </>
        )}

        {plan.rationale && (
          <Typography variant="body2" color="text.secondary">{plan.rationale}</Typography>
        )}

        {plan.nextRetestDue && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Next max-static re-test due {new Date(plan.nextRetestDue).toLocaleDateString()}
          </Typography>
        )}
      </Paper>

      <CheckInPanel
        sessions={sessions}
        windowDays={plan.checkInCadenceDays || 14}
        onDiscussWithNereus={digest => toChat(digestToPrompt(digest))}
      />
    </Stack>
  );
};

/** Log tab: BOLT trend plus session history, both read from the API. */
const LogTab: React.FC<{ reloadKey: number }> = ({ reloadKey }) => {
  const [sessions, setSessions] = useState<ApneaSessionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apneaService.getSessions({ limit: 200 })
      .then(result => { if (!cancelled) { setSessions(result); setError(null); } })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load your log.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const boltPoints = useMemo(
    () => sessions
      .filter(s => s.bolt)
      .map(s => ({
        date: s.startedAt,
        seconds: s.bolt!.seconds,
        manual: s.bolt!.source !== 'Timed',
      })),
    [sessions]
  );

  if (loading) {
    return (
      <Paper sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Stack spacing={2}>
      <BoltTrendChart points={boltPoints} />
      <SessionHistory sessions={sessions} />
    </Stack>
  );
};

const ApneaTraining: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentUser, setCurrentUser] = useState(() => authService.getUser());
  // Bumped after a save so the Log tab refetches rather than showing stale history.
  const [logVersion, setLogVersion] = useState(0);
  // Null while unknown (still loading). Until a plan exists, training is not
  // yet meaningful to run or review, so Train/Log stay locked and the page
  // opens on Plan instead of Today.
  const [hasPlan, setHasPlan] = useState<boolean | null>(null);

  useEffect(() => {
    return authService.subscribe(() => setCurrentUser(authService.getUser()));
  }, []);

  useEffect(() => {
    let cancelled = false;
    apneaService.getPlans()
      .then(plans => { if (!cancelled) setHasPlan(plans.length > 0); })
      .catch(() => { if (!cancelled) setHasPlan(false); });
    return () => { cancelled = true; };
  }, [logVersion]);

  const tabParam = searchParams.get('tab');
  const requestedTab: ApneaTab = isApneaTab(tabParam) ? tabParam : 'today';
  // No plan yet: force onto Plan and refuse the gated tabs even if requested
  // directly via URL, rather than rendering a locked tab's content underneath.
  const activeTab: ApneaTab = hasPlan === false && (!tabParam || PLAN_GATED_TABS.includes(requestedTab))
    ? 'plan'
    : requestedTab;

  const handleTabChange = (_event: React.SyntheticEvent, value: ApneaTab) => {
    setSearchParams({ tab: value }, { replace: true });
  };

  // Feature is admin-granted. Redirect a direct URL visit rather than rendering an empty shell.
  useEffect(() => {
    if (currentUser && !currentUser.hasApneaAccess) {
      navigate('/calendar', { replace: true });
    }
  }, [currentUser, navigate]);

  if (!currentUser?.hasApneaAccess) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <AppNavigation />
        <Alert severity="info">
          Apnea training is not enabled for your account.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <AppNavigation />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Air color="primary" />
        <Typography variant="h5">Apnea Training</Typography>
      </Box>

      <Alert severity="warning" sx={{ mb: 2 }}>
        Dry-land training only. Never hold your breath in or near water without a
        rescue-trained buddy, and never hold your breath on scuba.
      </Alert>

      {hasPlan === false && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Set up your training plan before running or reviewing sessions. It only takes
          your BOLT score (or a max-static estimate) and a few preferences.
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        {TABS.map(tab => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={tab.label}
            disabled={hasPlan === false && PLAN_GATED_TABS.includes(tab.value)}
          />
        ))}
      </Tabs>

      {activeTab === 'today' && (
        <TodayTab reloadKey={logVersion} onSaved={() => setLogVersion(v => v + 1)} />
      )}
      {activeTab === 'train' && <TrainTab onSaved={() => setLogVersion(v => v + 1)} />}
      {activeTab === 'plan' && (
        <PlanTab
          reloadKey={logVersion}
          onSaved={() => setLogVersion(v => v + 1)}
          onPlanStatus={setHasPlan}
        />
      )}
      {activeTab === 'log' && <LogTab reloadKey={logVersion} />}
      {activeTab === 'learn' && <LearnTab />}
    </Box>
  );
};

export default ApneaTraining;
